import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Send, RotateCcw, ChevronDown, Bot, User, Loader,
  Settings, Eye, EyeOff, Copy, Check, Zap, Database, Clock, Radio
} from 'lucide-react';

// ─── Markdown renderer (minimal, no external dep) ───────────────────────────
const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    // Markdown links [text](url) — must run before bare-URL pass
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;">$1</a>'
    )
    // Bare URLs not already inside an href="..."
    .replace(/(^|[^"'=])(https?:\/\/[^\s<>"']+)/g, (_, pre, url) => {
      const clean = url.replace(/[.,;!?)]+$/, '');
      return `${pre}<a href="${clean}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;">${clean}</a>`;
    })
    // HR separator ---
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e5e7eb;margin:8px 0"/>')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Bullet list items
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul style="margin:4px 0 4px 16px;padding:0">$1</ul>')
    // Numbered list items
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  return `<p style="margin:0">${html}</p>`;
};

const MessageBubble = ({ msg }) => {
  const isBot = msg.role === 'bot';
  const [copied, setCopied] = useState(false);

  const copyText = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: isBot ? 'flex-start' : 'flex-end',
      marginBottom: '12px',
      gap: '8px',
      alignItems: 'flex-start',
    }}>
      {isBot && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--primary)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Bot size={16} color="#fff" />
        </div>
      )}

      <div style={{ maxWidth: '75%', position: 'relative' }}>
        <div style={{
          background: isBot ? '#fff' : '#e8f0fe',
          color: '#1a1a1a',
          borderRadius: isBot ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
          padding: '10px 14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          fontSize: '14px',
          lineHeight: '1.6',
          border: isBot ? '1px solid var(--border)' : '1px solid #c7d9f8',
        }}>
          {isBot ? (
            <div
              dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
              style={{ margin: 0, color: '#1a1a1a' }}
            />
          ) : (
            <span style={{ color: '#1a1a1a' }}>{msg.content}</span>
          )}
          {msg.streaming && (
            <span style={{
              display: 'inline-block', width: 8, height: 14,
              background: 'var(--primary)', marginLeft: 2,
              animation: 'blink 0.7s step-end infinite', verticalAlign: 'text-bottom',
            }} />
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginTop: 4, justifyContent: isBot ? 'flex-start' : 'flex-end',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
          {isBot && !msg.streaming && (
            <button onClick={copyText} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center'
            }}>
              {copied ? <Check size={12} color="green" /> : <Copy size={12} />}
            </button>
          )}
          {isBot && msg.meta && !msg.streaming && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              · {msg.meta.context_chunks || 0} chunks · {msg.meta.timing?.total || '?'}s
            </span>
          )}
        </div>
      </div>

      {!isBot && (
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#6c757d', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <User size={16} color="#fff" />
        </div>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ChatPreview = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(true);

  const [conversations, setConversations] = useState(() => {
    try {
      const saved = localStorage.getItem('chatpreview_conversations');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);

  // Config panel
  const [showConfig, setShowConfig] = useState(false);
  const [useStreaming, setUseStreaming] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Persist conversations to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem('chatpreview_conversations', JSON.stringify(conversations));
    } catch { /* quota exceeded - ignore */ }
  }, [conversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations, selectedTenant]);

  useEffect(() => { fetchTenants(); }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchPrompt(selectedTenant.id);
    }
  }, [selectedTenant?.id]);

  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      const res = await axios.get('/api/tenant/');
      setTenants(res.data);
      if (res.data.length > 0) {
        const savedId = localStorage.getItem('admin_selected_tenant_id');
        const saved = savedId && res.data.find(t => t.id === savedId);
        setSelectedTenant(saved || res.data[0]);
      }
    } catch {
      toast.error('Failed to load tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const fetchPrompt = async (tenantId) => {
    setLoadingPrompt(true);
    try {
      const res = await axios.get(`/api/prompts/tenant/${tenantId}/prompt`);
      setPromptText(res.data.current_prompt || '');
    } catch {
      setPromptText('');
    } finally {
      setLoadingPrompt(false);
    }
  };

  const savePrompt = async () => {
    if (!selectedTenant) return;
    setSavingPrompt(true);
    try {
      await axios.put(`/api/prompts/tenant/${selectedTenant.id}/prompt`, { prompt: promptText });
      toast.success('Prompt salvat');
    } catch {
      toast.error('Eroare la salvare prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const getConversation = useCallback((tenantId) => {
    return conversations[tenantId] || { messages: [], sessionId: null };
  }, [conversations]);

  const startNewSession = () => {
    if (!selectedTenant) return;
    setConversations(prev => {
      const updated = { ...prev, [selectedTenant.id]: { messages: [], sessionId: null } };
      try { localStorage.setItem('chatpreview_conversations', JSON.stringify(updated)); } catch {}
      return updated;
    });
    toast.success('Sesiune nouă pornită');
    inputRef.current?.focus();
  };

  // ── Send message (non-streaming) ──────────────────────────────────────────
  const sendMessageNonStream = async (userMsg, tenantId, currentConv) => {
    const res = await axios.post('/api/chat/', {
      message: userMsg.content,
      session_id: currentConv.sessionId || undefined,
      stream: false,
    }, {
      headers: { 'X-API-Key': selectedTenant.api_key },
    });

    const botMsg = {
      role: 'bot',
      content: res.data.response,
      timestamp: new Date().toISOString(),
      meta: {
        context_chunks: res.data.context_length,
        strategy: res.data.strategy,
        timing: res.data.timing,
      }
    };

    setConversations(prev => ({
      ...prev,
      [tenantId]: {
        sessionId: res.data.session_id,
        messages: [...(prev[tenantId]?.messages || []), botMsg],
      }
    }));
  };

  // ── Send message (streaming via SSE) ─────────────────────────────────────
  const sendMessageStream = async (userMsg, tenantId, currentConv) => {
    const botMsgId = Date.now();

    // Insert empty bot message placeholder
    setConversations(prev => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        messages: [...(prev[tenantId]?.messages || []), {
          id: botMsgId,
          role: 'bot',
          content: '',
          timestamp: new Date().toISOString(),
          streaming: true,
        }]
      }
    }));

    const baseURL = axios.defaults.baseURL || '';
    const url = `${baseURL}/api/chat/`;
    const body = JSON.stringify({
      message: userMsg.content,
      session_id: currentConv.sessionId || undefined,
      stream: true,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': selectedTenant.api_key,
        'Authorization': axios.defaults.headers.common['Authorization'] || '',
      },
      body,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let newSessionId = currentConv.sessionId;
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep incomplete line

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const chunk = JSON.parse(line.slice(6));

          if (chunk.type === 'content') {
            setConversations(prev => {
              const msgs = [...(prev[tenantId]?.messages || [])];
              const idx = msgs.findIndex(m => m.id === botMsgId);
              if (idx !== -1) {
                msgs[idx] = { ...msgs[idx], content: msgs[idx].content + chunk.content };
              }
              return { ...prev, [tenantId]: { ...prev[tenantId], messages: msgs } };
            });
          } else if (chunk.type === 'done') {
            newSessionId = chunk.session_id || newSessionId;
            // Mark streaming done
            setConversations(prev => {
              const msgs = [...(prev[tenantId]?.messages || [])];
              const idx = msgs.findIndex(m => m.id === botMsgId);
              if (idx !== -1) {
                msgs[idx] = { ...msgs[idx], streaming: false };
              }
              return { ...prev, [tenantId]: { sessionId: newSessionId, messages: msgs } };
            });
          }
        } catch { /* ignore parse errors */ }
      }
    }
  };

  // ── Unified send ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!inputValue.trim() || !selectedTenant || sending) return;

    const userMsg = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    const tenantId = selectedTenant.id;
    const currentConv = getConversation(tenantId);

    setConversations(prev => ({
      ...prev,
      [tenantId]: {
        ...currentConv,
        messages: [...currentConv.messages, userMsg],
      }
    }));
    setInputValue('');
    setSending(true);

    try {
      if (useStreaming) {
        await sendMessageStream(userMsg, tenantId, currentConv);
      } else {
        await sendMessageNonStream(userMsg, tenantId, currentConv);
      }
    } catch (err) {
      toast.error('Eroare: ' + (err.response?.data?.detail || err.message));
      setConversations(prev => ({
        ...prev,
        [tenantId]: {
          ...prev[tenantId],
          messages: prev[tenantId]?.messages.filter(m => m !== userMsg) || [],
        }
      }));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentConv = selectedTenant ? getConversation(selectedTenant.id) : { messages: [], sessionId: null };
  const messages = currentConv.messages;

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Chat Preview</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '2px 0 0' }}>
            Testează conversațional orice tenant fără widget extern
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {loadingTenants ? (
            <Loader size={18} className="spin" />
          ) : (
            <div style={{ position: 'relative' }}>
              <select
                value={selectedTenant?.id || ''}
                onChange={e => {
                  const t = tenants.find(t => t.id === e.target.value);
                  setSelectedTenant(t);
                  if (t) localStorage.setItem('admin_selected_tenant_id', t.id);
                }}
                style={{
                  padding: '8px 32px 8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  background: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  appearance: 'none',
                  cursor: 'pointer',
                  minWidth: 180,
                  color: '#1a1a1a',
                }}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
            </div>
          )}

          <button
            onClick={() => setShowConfig(v => !v)}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: showConfig ? '#e8f0fe' : undefined }}
          >
            <Settings size={15} />
            Config
          </button>

          <button
            onClick={startNewSession}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={15} />
            Sesiune nouă
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>

        {/* Chat panel */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          background: '#f8f9fa', borderRadius: 12,
          border: '1px solid var(--border)', overflow: 'hidden',
        }}>
          {/* Chat header */}
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
            background: '#fff', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: selectedTenant ? '#22c55e' : '#94a3b8',
            }} />
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
              {selectedTenant ? selectedTenant.name : 'Niciun tenant selectat'}
            </span>
            {currentConv.sessionId && (
              <span style={{
                fontSize: 11, color: 'var(--text-muted)',
                background: '#f1f5f9', padding: '2px 8px', borderRadius: 20,
                fontFamily: 'monospace',
              }}>
                {currentConv.sessionId.slice(0, 16)}…
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Radio size={13} color={useStreaming ? '#22c55e' : '#94a3b8'} />
              <span style={{ fontSize: 12, color: useStreaming ? '#22c55e' : '#94a3b8', fontWeight: 500 }}>
                {useStreaming ? 'Streaming ON' : 'Streaming OFF'}
              </span>
            </div>
          </div>

          {/* Messages area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {messages.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: '100%', color: 'var(--text-muted)', gap: 12,
              }}>
                <Bot size={40} strokeWidth={1.2} />
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 600, margin: 0, color: '#1a1a1a' }}>Începe o conversație</p>
                  <p style={{ fontSize: 13, margin: '4px 0 0' }}>
                    Scrie un mesaj pentru a testa {selectedTenant?.name || 'tenantul selectat'}
                  </p>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <MessageBubble key={msg.id || idx} msg={msg} />
              ))
            )}

            {sending && !useStreaming && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--primary)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <Bot size={16} color="#fff" />
                </div>
                <div style={{
                  background: '#fff', border: '1px solid var(--border)',
                  borderRadius: '4px 12px 12px 12px',
                  padding: '10px 14px', display: 'flex', gap: 4, alignItems: 'center',
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1s infinite' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1s infinite 0.2s' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8', animation: 'pulse 1s infinite 0.4s' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid var(--border)',
            background: '#fff', display: 'flex', gap: 8, alignItems: 'flex-end',
          }}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedTenant ? `Scrie un mesaj pentru ${selectedTenant.name}…` : 'Selectează un tenant mai întâi'}
              disabled={!selectedTenant || sending}
              rows={1}
              style={{
                flex: 1, border: '1px solid var(--border)', borderRadius: 8,
                padding: '10px 12px', fontSize: 14, resize: 'none',
                fontFamily: 'inherit', lineHeight: '1.5',
                outline: 'none', transition: 'border-color 0.2s',
                color: '#1a1a1a', background: '#fff',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--primary)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || !selectedTenant || sending}
              className="btn btn-primary"
              style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {sending ? <Loader size={16} className="spin" /> : <Send size={16} />}
              Trimite
            </button>
          </div>
        </div>

        {/* Config panel */}
        {showConfig && selectedTenant && (
          <div style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Tenant info + streaming toggle */}
            <div className="card" style={{ padding: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                Tenant Info
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Row label="Nume" value={selectedTenant.name} />
                <Row label="Slug" value={selectedTenant.slug} mono />
                <div>
                  <label style={labelStyle}>API Key</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', flex: 1, color: '#1a1a1a' }}>
                      {showApiKey ? selectedTenant.api_key : '••••••••••••••••••••'}
                    </span>
                    <button onClick={() => setShowApiKey(v => !v)} style={iconBtnStyle}>
                      {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(selectedTenant.api_key); toast.success('Copiat!'); }} style={iconBtnStyle}>
                      <Copy size={14} />
                    </button>
                  </div>
                </div>

                {/* Streaming toggle */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Radio size={14} color={useStreaming ? '#22c55e' : '#94a3b8'} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>Streaming</span>
                    </div>
                    <button
                      onClick={() => setUseStreaming(v => !v)}
                      style={{
                        width: 44, height: 24, borderRadius: 12,
                        background: useStreaming ? '#22c55e' : '#cbd5e1',
                        border: 'none', cursor: 'pointer', position: 'relative',
                        transition: 'background 0.2s',
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 3,
                        left: useStreaming ? 23 : 3,
                        width: 18, height: 18, borderRadius: '50%',
                        background: '#fff', transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                    {useStreaming ? 'Răspunsul apare caracter cu caracter' : 'Răspunsul apare complet după procesare'}
                  </p>
                </div>

                {/* Session stats */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <StatBadge icon={<Database size={12} />} label="Docs" value={selectedTenant.document_count ?? '—'} />
                    <StatBadge icon={<Zap size={12} />} label="Mesaje" value={messages.length} />
                    <StatBadge icon={<Clock size={12} />} label="Sesiune" value={currentConv.sessionId ? 'activă' : 'nouă'} />
                  </div>
                </div>
              </div>
            </div>

            {/* Prompt editor card */}
            <div className="card" style={{ padding: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: 0 }}>
                  System Prompt
                </h3>
                <button
                  onClick={savePrompt}
                  disabled={savingPrompt || loadingPrompt}
                  className="btn btn-primary"
                  style={{ fontSize: 12, padding: '4px 12px' }}
                >
                  {savingPrompt ? <Loader size={12} className="spin" /> : 'Salvează'}
                </button>
              </div>

              {loadingPrompt ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                  <Loader size={14} className="spin" /> Se încarcă promptul…
                </div>
              ) : (
                <textarea
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  style={{
                    flex: 1, border: '1px solid var(--border)', borderRadius: 8,
                    padding: '10px 12px', fontSize: 12, resize: 'none',
                    fontFamily: 'monospace', lineHeight: '1.6',
                    outline: 'none', minHeight: 300,
                    color: '#1a1a1a', background: '#fff',
                  }}
                  placeholder="System prompt pentru acest tenant…"
                />
              )}

              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                Modificările se aplică la următoarea sesiune nouă.
              </p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ─── Small helpers ───────────────────────────────────────────────────────────
const labelStyle = { fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 };
const iconBtnStyle = {
  background: 'none', border: '1px solid var(--border)', borderRadius: 4,
  padding: '2px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
  color: 'var(--text-muted)',
};

const Row = ({ label, value, mono }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <span style={{ fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', color: '#1a1a1a' }}>{value}</span>
  </div>
);

const StatBadge = ({ icon, label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--text-muted)', fontSize: 11 }}>
      {icon} {label}
    </div>
    <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>{value}</span>
  </div>
);

export default ChatPreview;
